import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  Output,
} from '@angular/core';
import { FileDetails } from '../../models/file-details';
import { FileOperation } from '../../models/file-operation';

import { PopupComponent } from '../../../../shared/components/popup/popup.component';
import { FileUploadService } from '../../services/file-upload.service';
import { Subject, takeUntil } from 'rxjs';

import { SvgIconComponent } from '../../../../shared/components/svg-icon/svg-icon.component';
import { CommonService } from '../../../../shared/services/common/common.service';
import { ToastType } from '../../../../shared/interfaces/common';

@Component({
  selector: 'app-document-operations-modal',
  standalone: true,
  imports: [CommonModule, PopupComponent, SvgIconComponent],
  templateUrl: './document-operations-modal.component.html',
  styleUrl: './document-operations-modal.component.scss',
})
export class DocumentOperationsModalComponent implements OnDestroy {
  @Input() file!: FileDetails;
  @Input() fileOperations: FileOperation[] = [];

  get isPortfolioMiningAvailable(): boolean {
    return this.fileOperations.some(
      (op) => op.operation_name === 'Portfolio Mining',
    );
  }
  @Output() closeModal = new EventEmitter<void>();
  @Output() operationCompleted = new EventEmitter<void>();

  fileUploadService = inject(FileUploadService);
  commonService = inject(CommonService);
  isRetriggering = false;
  destroy$ = new Subject<void>();

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onClose(): void {
    this.closeModal.emit();
  }

  onRetrigger(fileId: number): void {
    if (this.isRetriggering) return;

    this.isRetriggering = true;
    // Hardcoding PDM operation ID as 3 for now, as per requirement
    this.fileUploadService
      .retriggerValidation(fileId, [3])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isRetriggering = false;
          this.commonService.triggerToast({
            type: ToastType.Success,
            message: 'Portfolio Mining triggered successfully.',
          });
          this.operationCompleted.emit();
        },
        error: (err) => {
          this.isRetriggering = false;
          const errorMessage =
            err.error?.detail ||
            err.error?.message ||
            'Failed to re-trigger Portfolio Mining.';
          this.commonService.triggerToast({
            type: ToastType.Error,
            message: errorMessage,
          });
        },
      });
  }

  getStatusClass(status: string): string {
    const statusLower = status.toLowerCase();
    if (statusLower.includes('success') || statusLower.includes('completed')) {
      return 'status-success';
    } else if (
      statusLower.includes('failed') ||
      statusLower.includes('error')
    ) {
      return 'status-failed';
    } else if (
      statusLower.includes('progress') ||
      statusLower.includes('processing')
    ) {
      return 'status-progress';
    }
    return 'status-pending';
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  hasErrors(operation: any): boolean {
    if (!operation.errors) return false;

    const errors = operation.errors;
    return (
      (errors.missing_columns && errors.missing_columns.length > 0) ||
      (errors.missing_fields && errors.missing_fields.length > 0) ||
      (errors.messages && errors.messages.length > 0)
    );
  }
}
